import React, { useEffect, useState, useCallback } from "react";
import { X, Download, Loader2, CheckCircle2 } from "lucide-react";
import { getUtm } from "../utils/utm";
import { trackConversion } from "../utils/analytics";

const DISMISS_KEY = "exit_popup_dismissed_v1";
const DISMISS_DAYS = 7; // don't nag returning visitors for a week

/**
 * Exit-intent popup that shows the lead-magnet form to recover visitors about to leave.
 * - Desktop trigger: mouse leaves the viewport near the top (browser bar / tab area)
 * - Mobile trigger: rapid scroll-up gesture after significant scroll depth
 * - Fires ONCE per session and remembers dismissal for DISMISS_DAYS days.
 */
export function ExitIntentPopup() {
  const [open, setOpen] = useState(false);
  const [alreadyTriggered, setAlreadyTriggered] = useState(false);

  const wasDismissed = useCallback(() => {
    try {
      const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
      if (!ts) return false;
      return Date.now() - ts < DISMISS_DAYS * 24 * 3600 * 1000;
    } catch {
      return false;
    }
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (err) { console.warn("[exit-intent] opslaan mislukt:", err?.message); }
    setOpen(false);
  };

  useEffect(() => {
    if (wasDismissed()) return;
    if (alreadyTriggered) return;

    const isMobile = window.matchMedia("(max-width: 768px)").matches;

    // Desktop: detect mouseleave near top
    const onMouseLeave = (e) => {
      if (alreadyTriggered) return;
      if (e.clientY < 5 && e.relatedTarget === null) {
        setAlreadyTriggered(true);
        setOpen(true);
      }
    };

    // Mobile: detect rapid scroll-up after user scrolled >40% down
    let lastY = window.scrollY;
    let peakScroll = 0;
    const onScroll = () => {
      if (alreadyTriggered) return;
      const y = window.scrollY;
      const pageH = Math.max(document.body.scrollHeight - window.innerHeight, 1);
      peakScroll = Math.max(peakScroll, y / pageH);
      if (peakScroll > 0.4 && y < lastY - 200) {
        setAlreadyTriggered(true);
        setOpen(true);
      }
      lastY = y;
    };

    // Also: fallback timer — show after 60 s if user still hasn't converted
    const idleTimer = setTimeout(() => {
      if (!alreadyTriggered && !wasDismissed()) {
        setAlreadyTriggered(true);
        setOpen(true);
      }
    }, 60_000);

    if (isMobile) {
      window.addEventListener("scroll", onScroll, { passive: true });
    } else {
      document.addEventListener("mouseleave", onMouseLeave);
    }
    return () => {
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("mouseleave", onMouseLeave);
      clearTimeout(idleTimer);
    };
  }, [alreadyTriggered, wasDismissed]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] bg-m-navy/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fadein"
      onClick={dismiss}
      data-testid="exit-intent-overlay"
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 md:p-8 animate-scalein"
        onClick={(e) => e.stopPropagation()}
        data-testid="exit-intent-popup"
      >
        <button
          onClick={dismiss}
          className="absolute top-4 right-4 text-m-muted hover:text-m-navy transition-colors"
          aria-label="Sluiten"
          data-testid="exit-intent-close"
        >
          <X size={20} />
        </button>
        <div className="inline-flex items-center gap-2 text-xs font-semibold text-m-orange uppercase tracking-wider mb-3">
          <Download size={14} /> Wacht even!
        </div>
        <h2 className="text-2xl md:text-3xl font-bold text-m-navy leading-tight tracking-tight">
          Krijg gratis onze <span className="text-m-orange">Renovatie&#8209;checklist 2026</span>
        </h2>
        <p className="mt-3 text-sm text-m-text-2 leading-relaxed">
          20 punten die u <strong>duizenden euros</strong> kunnen besparen bij een renovatie.
          Direct in uw inbox als PDF. Geen spam, wij bellen u niet op.
        </p>
        <div className="mt-5">
          <ExitForm onSuccess={dismiss} />
        </div>
      </div>
    </div>
  );
}

function ExitForm({ onSuccess }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [state, setState] = useState({ loading: false, done: false, error: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return;
    setState({ loading: true, done: false, error: "" });
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/lead-magnet`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, source_page: "/exit-intent", ...getUtm() }),
      });
      if (!res.ok) throw new Error("Er ging iets mis. Probeer het opnieuw.");
      trackConversion("lead_magnet", { source_page: "/exit-intent", value: 15 });
      setState({ loading: false, done: true, error: "" });
      setTimeout(onSuccess, 3500);
    } catch (err) {
      setState({ loading: false, done: false, error: err.message || "Fout" });
    }
  };

  if (state.done) {
    return (
      <div className="flex flex-col items-center gap-3 text-center py-4" data-testid="exit-intent-success">
        <CheckCircle2 size={40} className="text-m-orange" />
        <p className="text-m-navy font-semibold">Bedankt! Uw checklist is onderweg.</p>
        <p className="text-xs text-m-text-2">Check uw e-mail (ook de spam-folder).</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="exit-intent-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input
          type="text"
          required
          placeholder="Naam"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          className="px-4 py-3 rounded-full border border-m-border text-sm text-m-navy focus:border-m-orange focus:outline-none"
          data-testid="exit-intent-name"
        />
        <input
          type="tel"
          placeholder="Telefoon (optioneel)"
          value={form.phone}
          onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
          className="px-4 py-3 rounded-full border border-m-border text-sm text-m-navy focus:border-m-orange focus:outline-none"
          data-testid="exit-intent-phone"
        />
      </div>
      <input
        type="email"
        required
        placeholder="E-mail"
        value={form.email}
        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
        className="w-full px-4 py-3 rounded-full border border-m-border text-sm text-m-navy focus:border-m-orange focus:outline-none"
        data-testid="exit-intent-email"
      />
      <button
        type="submit"
        disabled={state.loading}
        className="w-full bg-m-orange hover:bg-m-orange-hover text-white font-semibold py-3 rounded-full transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-70"
        data-testid="exit-intent-submit"
      >
        {state.loading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
        Stuur mij de checklist
      </button>
      {state.error && <p className="text-xs text-red-600 text-center" data-testid="exit-intent-error">{state.error}</p>}
      <p className="text-[10px] text-m-muted text-center">Geen spam. U kunt zich altijd uitschrijven.</p>
    </form>
  );
}


export default ExitIntentPopup;
